import { storeToRefs } from 'pinia'
import { computed, Ref, ComputedRef } from 'vue'

import { useStoreSettingsForwarding } from '@/entities/forwarding'

import { useLocalization } from '@/shared/composables/localization'
import { useUserFeatures } from '@/shared/composables/user-features'
import { ScenarioValue, SubscriberServices } from '@/shared/types/settings'

interface ServiceItem {
  label: string,
  value: string,
}

interface ScenarioItem {
  label: string,
  value: string,
}

export interface ResultUseCallSettings {
  subscriberServices: Ref<Map<string, SubscriberServices>>,
  scenarios: Ref<Map<string, ScenarioValue>>,
  services: ComputedRef<ServiceItem[]>,
  getScenariosList: (key: string) => ScenarioItem[] | undefined,
}

export const useCallSettings = (): ResultUseCallSettings => {
  const storeSettingsForwarding = useStoreSettingsForwarding()
  const { subscriberServices, scenarios } = storeToRefs(storeSettingsForwarding)
  const { t } = useLocalization()
  const { hasFeature } = useUserFeatures()

  const servicesLabels = computed(() => ({
    [SubscriberServices.BlackWhiteList]: t('mainAside.blackWhiteList'),
    [SubscriberServices.DoNotDisturb]: t('common.dontDisturb'),
    [SubscriberServices.FollowMe]: t('callsSettings.followMe'),
    [SubscriberServices.Forward]: t('common.redirection'),
    [SubscriberServices.IVR]: t('callsSettings.IVR'),
    [SubscriberServices.VoiceMailForwarding]: t('common.voiceMailForwarding'),
  }))

  const services = computed(() => {
    const services: ServiceItem[] = []
    subscriberServices.value.forEach((value: SubscriberServices, key: string) => {
      if (!hasFeature(value)) return
      services.push({
        label: servicesLabels.value[value],
        value: key,
      })
    })
    return services
  })

  const getScenariosList = (serviceKey: string) => {
    const scenario = scenarios.value.get(serviceKey)

    if (!scenario) return

    return Object.keys(scenario).map((key: string) => ({
      label: scenario[key]!,
      value: key,
    }))
  }

  return {
    subscriberServices,
    scenarios,
    services,
    getScenariosList,
  }
}
